import { Alert, AlertIcon, Box, Button, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { SourcingEventStatus, type SourcingEventDto } from "../../../autogen/bff-api";
import { EventChangeEditor } from "./EventChangeEditor";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import { ChangeLogList } from "./ChangeLogList";
import { useChangeFlow } from "./useChangeFlow";

interface Props {
  event: SourcingEventDto;
}

export const ChangeNoticeSection = ({ event }: Props) => {
  const { t } = useTranslation();
  const change = useChangeFlow(event);
  const canManage = useCanManageSourcingEvent(event);
  const { changesRequest, pending, published } = change;
  const cancelled = event.status === SourcingEventStatus.Cancelled;
  const canStartChange = change.canStartChange && canManage && !cancelled;
  const draft = canManage && !cancelled ? change.draft : null;

  if (!canStartChange && !draft && !pending && published.length === 0) return null;

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Flex align="center" justify="space-between" mb={published.length > 0 || draft || pending ? "4" : "0"}>
        <Heading as="h3" size="sm">
          {changesRequest ? t("Changes to the request") : t("Changes to the announcement")}
        </Heading>
        {canStartChange && (
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            onClick={change.startChange}
            isLoading={change.isStarting}
          >
            {change.actionLabel}
          </Button>
        )}
        {draft && (
          <Button size="sm" colorScheme="teal" onClick={change.openChange}>
            {change.actionLabel}
          </Button>
        )}
      </Flex>

      <Stack spacing="4">
        {pending && (
          <Alert status="info" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <Box>
              <Text>{t("A change notice has been submitted and is awaiting confirmation from Doffin.")}</Text>
              <Text color="smMuted">{pending.changeDescription}</Text>
            </Box>
          </Alert>
        )}

        {draft && change.editor.isOpen && (
          <EventChangeEditor
            key={draft.id}
            event={event}
            draft={draft}
            isOpen={change.editor.isOpen}
            onClose={change.editor.onClose}
          />
        )}

        {published.length > 0 && <ChangeLogList revisions={published} />}
      </Stack>
    </Box>
  );
};
